// The ten runs of Summit Run, shortest to tallest, ending where the year does.
//
// You ski DOWN them. The climbing is the part the app already tracks all year,
// so making the game a descent gives it a reason to exist: you earned the ride
// down by doing the work up. It also makes the automatic forward motion honest
// instead of arbitrary, because gravity is doing it.
//
// Real mountains with real heights, because a kid who plays this for a term
// ends up knowing that Denali is taller than Kilimanjaro, and that is a nicer
// thing to absorb by accident than a set of invented level names.
export interface Mountain {
  name: string;
  where: string;
  feet: number;
  // Difficulty knobs, tuned so the first one is winnable by a six-year-old on
  // their second try and the last one genuinely is not.
  speed: number;      // how fast the slope comes at you
  hazards: number;    // trees, rocks and moguls per second
  wind: number;       // crosswind, in px per frame
  fact: string;
  // The peak's actual outline, left base to right base, in a 0..1 box where
  // y=1 is the summit. Traced from each mountain's real profile, so Fuji is a
  // symmetrical cone, the Matterhorn is a hooked horn with one long shoulder,
  // Kilimanjaro is flat on top and Elbrus has its two summits. It costs about
  // twelve numbers per mountain and it is the difference between ten levels
  // and ten different mountains.
  profile: [number, number][];
}

export const MOUNTAINS: Mountain[] = [
  { name: "Mount Fuji", where: "Japan", feet: 12_389, speed: 1.7, hazards: 2, wind: 0,
    fact: "It is a volcano, and people climb it overnight to watch the sunrise from the top." ,
    profile: [[0,0],[.18,.28],[.34,.55],[.44,.82],[.47,.95],[.53,.95],[.56,.82],[.66,.55],[.82,.28],[1,0]] },
  { name: "Mount Rainier", where: "Washington", feet: 14_411, speed: 1.9, hazards: 2, wind: 0.1,
    fact: "It has 25 glaciers on it, more than any other mountain in the lower 48 states." ,
    profile: [[0,0],[.12,.22],[.26,.45],[.36,.66],[.44,.86],[.5,.95],[.58,.97],[.64,.88],[.74,.62],[.86,.3],[1,0]] },
  { name: "Matterhorn", where: "Switzerland", feet: 14_692, speed: 2.1, hazards: 3, wind: 0.15,
    fact: "Its four steep sides face almost exactly north, south, east and west." ,
    profile: [[0,0],[.22,.26],[.34,.5],[.42,.76],[.46,.95],[.5,1],[.56,.9],[.64,.66],[.76,.4],[.88,.18],[1,0]] },
  { name: "Mont Blanc", where: "France and Italy", feet: 15_774, speed: 2.3, hazards: 3, wind: 0.2,
    fact: "The name means White Mountain, and the summit is a dome of ice that changes height each year." ,
    profile: [[0,0],[.14,.26],[.28,.5],[.4,.72],[.5,.86],[.6,.9],[.7,.8],[.8,.58],[.9,.3],[1,0]] },
  { name: "Mount Elbrus", where: "Russia", feet: 18_510, speed: 2.5, hazards: 3, wind: 0.3,
    fact: "It is the tallest mountain in Europe, and it has two summits." ,
    profile: [[0,0],[.14,.3],[.28,.62],[.36,.86],[.4,.94],[.46,.9],[.5,.78],[.56,.86],[.62,.97],[.7,.84],[.82,.5],[1,0]] },
  { name: "Kilimanjaro", where: "Tanzania", feet: 19_341, speed: 2.7, hazards: 4, wind: 0.3,
    fact: "You walk from jungle to glacier in a few days, passing through five different climates." ,
    profile: [[0,0],[.1,.16],[.24,.36],[.36,.56],[.44,.7],[.52,.74],[.62,.74],[.72,.66],[.82,.44],[.92,.2],[1,0]] },
  { name: "Denali", where: "Alaska", feet: 20_310, speed: 2.9, hazards: 4, wind: 0.45,
    fact: "Measured from its own base, it is a taller climb than Everest." ,
    profile: [[0,0],[.1,.2],[.2,.42],[.3,.58],[.38,.72],[.46,.9],[.52,.97],[.6,.9],[.66,.76],[.76,.6],[.86,.34],[1,0]] },
  { name: "Aconcagua", where: "Argentina", feet: 22_838, speed: 3.1, hazards: 4, wind: 0.5,
    fact: "The tallest mountain outside of Asia, and the winds up top are nicknamed the White Wind." ,
    profile: [[0,0],[.12,.3],[.22,.52],[.3,.68],[.38,.8],[.44,.76],[.5,.88],[.58,.98],[.66,.86],[.74,.64],[.86,.34],[1,0]] },
  { name: "K2", where: "Pakistan and China", feet: 28_251, speed: 3.4, hazards: 5, wind: 0.65,
    fact: "Shorter than Everest and much harder. Climbers call it the Savage Mountain." ,
    profile: [[0,0],[.14,.22],[.26,.44],[.36,.64],[.44,.84],[.5,1],[.56,.84],[.64,.64],[.74,.44],[.86,.22],[1,0]] },
  { name: "Everest", where: "Nepal and Tibet", feet: 29_032, speed: 3.7, hazards: 5, wind: 0.8,
    fact: "At the top there is only a third as much oxygen in each breath as there is at home." ,
    profile: [[0,0],[.1,.24],[.2,.44],[.28,.56],[.36,.7],[.42,.66],[.5,.78],[.58,.92],[.63,1],[.7,.88],[.78,.66],[.88,.38],[1,0]] },
];

// Three climbs a day. Not a number pulled out of the air: it is short enough
// that the game cannot eat the afternoon the app just handed back, and long
// enough that a bad first run is not the end of it.
export const CLIMBS_PER_DAY = 3;

export const goodPickups = ["rope", "apple", "medkit", "oxygen"] as const;
export type Pickup = (typeof goodPickups)[number];

export const PICKUP_ART: Record<Pickup, { icon: string; label: string }> = {
  rope: { icon: "🪢", label: "Rope" },
  apple: { icon: "🍎", label: "Snack" },
  medkit: { icon: "🩹", label: "Bandage" },
  oxygen: { icon: "🫧", label: "Oxygen" },
};

// ---- Scoring ----
//
// Weighted so the thing worth chasing is a good run rather than a long one.
// Distance alone tops out at 100; a kid who threads the ramps and lands flips
// on the same hill will roughly triple that, which is what makes going for it
// better than playing safe.
//
// Computed on the server from the run's parts. The client reports what it did,
// never what it scored.
export const SCORE = {
  perPercent: 1,
  perCoin: 5,
  perFlag: 10,
  perFlip: 25,
  finished: 100,
  // Harder mountains are worth more, so a personal best on Everest is not
  // quietly worth the same as one on Fuji.
  mountainStep: 0.1,
} as const;

export function scoreRun(input: {
  mountainIndex: number; reached: number; summited: boolean;
  coins: number; flags: number; flips: number;
}): number {
  const clamp = (n: number, max: number) => Math.max(0, Math.min(max, Math.floor(n)));
  const base = clamp(input.reached, 100) * SCORE.perPercent
    + clamp(input.coins, 300) * SCORE.perCoin
    + clamp(input.flags, 80) * SCORE.perFlag
    + clamp(input.flips, 80) * SCORE.perFlip
    + (input.summited ? SCORE.finished : 0);
  const index = clamp(input.mountainIndex, MOUNTAINS.length - 1);
  return Math.floor(base * (1 + index * SCORE.mountainStep));
}

// ---- Light ----
//
// Alto's Adventure gets nearly all of its beauty from two things, and neither
// is expensive: everything in the foreground is a flat silhouette, and the sky
// is a gradient that changes with the time of day. Give each mountain its own
// hour and the ten runs stop being one run with the numbers turned up.
//
// Order walks from dawn on Fuji through noon, golden hour, dusk, night and back
// to a cold blue sunrise on Everest, so the set reads as a single day on the
// mountain rather than ten unrelated colour schemes.
export interface Palette {
  sky: [string, string, string];   // top, middle, horizon
  sun: string;                     // sun or moon disc
  sunY: number;                    // 0 = at the top of the sky, 1 = at the horizon
  far: string;                     // most distant ridge
  near: string;                    // nearer ridge
  peak: string;                    // the mountain you are on
  snow: [string, string];          // lit face, shaded base
  ink: string;                     // every foreground silhouette
  stars: number;                   // 0 to 1
}

export const PALETTES: Palette[] = [
  // Fuji · first light
  { sky: ["#1E3A63", "#5B7BA8", "#F0B48A"], sun: "#FFE3B8", sunY: 0.82,
    far: "#7E96BA", near: "#4E6890", peak: "#3B5478", snow: ["#FDF3EA", "#B9C8DE"], ink: "#22304A", stars: 0.25 },
  // Rainier · clear morning
  { sky: ["#2E6FB7", "#6BA6DC", "#CFE6F5"], sun: "#FFFFFF", sunY: 0.22,
    far: "#9DBEDA", near: "#6C93B8", peak: "#547EA6", snow: ["#FFFFFF", "#C4D6E8"], ink: "#20364C", stars: 0 },
  // Matterhorn · high blue noon
  { sky: ["#1F63B8", "#57A0DE", "#D8ECF8"], sun: "#FFFFFF", sunY: 0.12,
    far: "#A6C6DF", near: "#7099BE", peak: "#4F7CA5", snow: ["#FFFFFF", "#BFD4E8"], ink: "#1B3049", stars: 0 },
  // Mont Blanc · hazy afternoon
  { sky: ["#3E7FB4", "#8FBBD8", "#F3E3C8"], sun: "#FFF6DC", sunY: 0.34,
    far: "#B6CBDA", near: "#87A6BE", peak: "#63849F", snow: ["#FFFDF6", "#C8D6E2"], ink: "#243B50", stars: 0 },
  // Elbrus · golden hour
  { sky: ["#2D5C93", "#D2864F", "#F6C77E"], sun: "#FFD79A", sunY: 0.72,
    far: "#C69477", near: "#8E6A65", peak: "#6A5468", snow: ["#FFEAD2", "#C3A8AE"], ink: "#2A2438", stars: 0.1 },
  // Kilimanjaro · sunset
  { sky: ["#3A2C63", "#B4557A", "#F09265"], sun: "#FFC28A", sunY: 0.86,
    far: "#A9678A", near: "#77507A", peak: "#573F6A", snow: ["#FFDCC8", "#B295A8"], ink: "#241C36", stars: 0.3 },
  // Denali · dusk
  { sky: ["#221A47", "#5A3C7A", "#C4707C"], sun: "#F5A98F", sunY: 0.92,
    far: "#7A5484", near: "#513A6B", peak: "#3A2C57", snow: ["#F3D2CE", "#9C8AA6"], ink: "#191333", stars: 0.55 },
  // Aconcagua · deep twilight
  { sky: ["#101034", "#2E2A62", "#6B4A78"], sun: "#D8CBF0", sunY: 0.68,
    far: "#4A4079", near: "#332B5C", peak: "#241E45", snow: ["#DCD5F0", "#8880AE"], ink: "#0E0B24", stars: 0.85 },
  // K2 · full night
  { sky: ["#060A22", "#111A44", "#28356B"], sun: "#EDF2FF", sunY: 0.3,
    far: "#2C3A6E", near: "#1D2853", peak: "#141C3E", snow: ["#C9D4F2", "#6E79A8"], ink: "#070A1C", stars: 1 },
  // Everest · cold sunrise, back where the set began
  { sky: ["#123055", "#3D74A8", "#EAC59C"], sun: "#FFF0D4", sunY: 0.78,
    far: "#8AA6C4", near: "#5C7C9E", peak: "#42607F", snow: ["#FFF7EC", "#B6C6DC"], ink: "#1A2740", stars: 0.35 },
];

export function paletteFor(index: number): Palette {
  return PALETTES[Math.max(0, Math.min(PALETTES.length - 1, Math.floor(index)))]!;
}
